import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as styles from './report-detail-modal.module.scss';
import CustomAntModal from '../../../../components/CustomAntModal/CustomAntModal';
import talentProfileApi from '../../../../services/talentProfiles';
import { Spin } from 'antd';
import { useSelector } from 'react-redux';
import { BasicTable } from '../../../../assets/common';
import PrintReportDetailBtn from './PrintReportDetailBtn/PrintReportDetailBtn';
import moment from 'moment';

const MODAL_STYLE = {
  closePosition: 30,
  headerPaddingBottom: 30,
  titleFontSize: 20,
  containerPaddingLeftRight: 0,
  containerPaddingLeftRightCustom: 30,
};

const TABLE_STYLE = {
  headerWeight: 700,
  headerBackground: '#00a19c',
  headerColor: 'white',
};

const FEEDBACK_COLUMNS = [
  {
    title: 'Role',
    width: '20%',
    dataIndex: 'role',
    render: (text) => <div className={styles.text}>{text || '-'}</div>,
  },
  {
    title: 'Appreciative Feedback (Strengths)',
    width: '40%',
    dataIndex: 'strengths',
    render: (text) => (
      <div className={styles.text_feedback}>
        {Array.isArray(text) && text.length > 0 ? text.map((data, index) => <div key={index}>{data.bepcb_feedback}</div>) : '-'}
      </div>
    ),
  },
  {
    title: 'Constructive Feedback (Development Area)',
    width: '40%',
    dataIndex: 'development_area',
    render: (text) => (
      <div className={styles.text_feedback}>
        {Array.isArray(text) && text.length > 0 ? text.map((data, index) => <div key={index}>{data.bepcb_feedback}</div>) : '-'}
      </div>
    ),
  },
];

const ReportDetailModal = ({ selected, setSelected, setIsOpenViewMore }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});
  const { roleId } = useSelector((state) => state.user.roleActive);
  const printRef = useRef();
  const [reportFeedback, setReportFeedback] = useState([]);

  useEffect(() => {
    if (!selected) return;
    setOpen(true);
    setIsOpenViewMore(false);
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await Promise.all([
          talentProfileApi.getReportDetail(roleId, selected),
          talentProfileApi.getBePCBReportFeedback(roleId, selected),
        ]);
        if (res[0].status === 200 && res[1].status === 200) {
          setData(res[0].data.result);
          setReportFeedback(res[1].data.result.behavior_feedback);
        }
      } catch (error) {
        console.error(error);
        // setData({});
        // setReportFeedback([]);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selected]);

  useEffect(() => {
    if (!open) setSelected(null);
  }, [open]);

  const RESULT_COLUMNS = [
    {
      title: 'PETRONAS Cultural Beliefs Behaviors Result',
      width: '18%',
      dataIndex: 'behavior_result',
      render: (text, _, index) => {
        const isLast = index === data?.behavior_results?.length - 1;
        return <div className={isLast ? styles.text_highlighted : styles.text}>{text || '-'}</div>;
      },
    },
    {
      title: 'Manager(s)',
      width: '20%',
      dataIndex: 'manager',
      render: (text) => <div className={styles.text}>{text || '-'}</div>,
    },
    {
      title: 'Subordinate(s)*',
      width: '20%',
      dataIndex: 'subordinate',
      render: (text) => <div className={styles.text}>{text || '-'}</div>,
    },
    {
      title: 'Team Member(s)*',
      width: '20%',
      dataIndex: 'team_member',
      render: (text) => <div className={styles.text}>{text || '-'}</div>,
    },
    {
      title: 'Overall Result',
      width: '20%',
      dataIndex: 'overall_result',
      render: (text, _, index) => {
        const isLast = index === data?.behavior_results?.length - 1;
        return <div className={isLast ? styles.text_highlighted : styles.text}>{text || '-'}</div>;
      },
    },
  ];

  const isRefReady = useCallback((node) => {
    if (node !== null) {
      return node;
    }
  }, []);

  const addPageTitles = () => {
    const pages = document.querySelectorAll('.pageToPrint');
    pages.forEach((page, index) => {
      if (index === 0) {
        pages[index].style.paddingTop = '50px';
      }
      const footer = document.createElement('div');
      footer.className = 'temp-page-title';
      footer.style.position = 'absolute';
      footer.style.top = '0';
      footer.style.left = '30px';
      footer.style.fontSize = '20px';
      footer.style.fontWeight = '700';
      footer.style.color = '#3F3C4C';
      footer.textContent = `Employee BePCB Report for FY${selected?.year}`;
      page.appendChild(footer);
    });
  };

  const addPageLabels = () => {
    const pages = document.querySelectorAll('.pageToPrint');
    pages.forEach((page) => {
      const footer = document.createElement('div');
      footer.className = 'temp-author-label';
      footer.style.position = 'fixed';
      footer.style.bottom = '0';
      footer.style.left = '30px';
      footer.style.fontSize = '13px';
      footer.style.color = '#000';
      footer.textContent = `Printed in myTalentX on ${moment(new Date()).format('DD/MM/YYYY')} ${moment(new Date()).format('hh:mm A')} by ${
        data?.employee_name || 'N/A'
      }`;
      page.appendChild(footer);
    });
  };

  const handleBeforePrint = () => {
    document.body.classList.add('body-print');
    addPageTitles();
    addPageLabels();
  };

  const handleAfterPrint = () => {
    const tempPageTitle = document.querySelectorAll('.temp-page-title');
    tempPageTitle.forEach((element) => element.remove());
    const tempAuthorLabel = document.querySelectorAll('.temp-author-label');
    tempAuthorLabel.forEach((element) => element.remove());
    document.body.classList.remove('body-print');

    const pages = document.querySelectorAll('.pageToPrint');
    pages.forEach((page, index) => {
      if (index === 0) {
        pages[index].style.paddingTop = '0';
      }
    });
  };

  return (
    <div>
      <CustomAntModal
        hideDefaultBtn
        open={open}
        setOpen={setOpen}
        styles={MODAL_STYLE}
        width={1300}
        closeType={1}
        title={`Employee BePCB Report for FY${selected?.year}`}
      >
        <Spin spinning={loading}>
          <div className={`pageToPrint`} ref={printRef} style={{ padding: '0 30px' }}>
            <div className={styles.block}>
              <div className={styles.title}>Employee Details</div>
              <div className={styles.info}>
                <div className={styles.left}>
                  <span>Employee ID: {data.employee_id || 'N/A'}</span>
                  <span>Employee Name: {data.employee_name || 'N/A'}</span>
                  <span>Employee Position Title: {data.position_title || 'N/A'}</span>
                  <span>Be PCB Overall Rating (report generated): {data.bepcb_report_generated || 'N/A'}</span>
                  <span>Be PCB Overall Rating (after YEPR): {data.bepcb_after_yepr || 'N/A'}</span>
                </div>
                <div className={styles.right}>
                  <span>Department: {data.department || 'N/A'}</span>
                  <span>Company: {data.company || 'N/A'}</span>
                  <span>Division: {data.division || 'N/A'}</span>
                  <span>Business Unit: {data.business_unit || 'N/A'}</span>
                </div>
              </div>
            </div>
            <div className={styles.block}>
              <div className={styles.title}>PETRONAS Cultural Beliefs Behaviors Result</div>
              <BasicTable
                rowKey={(record) => `${record.behavior_result}${record.manager}`}
                dataSource={data.behavior_results}
                columns={RESULT_COLUMNS}
                pagination={false}
                styles={TABLE_STYLE}
              />
            </div>
            <div className={styles.block}>
              <div className={styles.title}>PETRONAS Cultural Beliefs Behaviors Feedback</div>
              <BasicTable
                rowKey={(record) => `${record.behavior_result}${record.manager}`}
                dataSource={reportFeedback}
                columns={FEEDBACK_COLUMNS}
                pagination={false}
                styles={TABLE_STYLE}
                rowClassName={() => styles.row_top}
              />
            </div>
          </div>
          <div className={styles.btn_wrapper} ref={isRefReady}>
            <PrintReportDetailBtn
              componentRef={printRef}
              selectedItem={selected}
              style={{ backgroundColor: ' #00a19c', color: 'white', width: '100px' }}
              handleBeforePrint={handleBeforePrint}
              handleAfterPrint={handleAfterPrint}
            />
          </div>
        </Spin>
      </CustomAntModal>
    </div>
  );
};

export default ReportDetailModal;

ReportDetailModal.propTypes = {
  selected: PropTypes.string,
  setSelected: PropTypes.func,
  setIsOpenViewMore: PropTypes.func,
};
